import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Trash2, LogOut, Mail, RefreshCw, Lock, Star, Check, Clock, MessageSquareQuote } from "lucide-react";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "wv_admin_token";

const field =
  "w-full bg-transparent border-b border-border py-4 text-lg text-ink placeholder:text-stone/40 focus:border-bronze outline-none transition-colors duration-300";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [loggingIn, setLoggingIn] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [tab, setTab] = useState("messages");
  const [loading, setLoading] = useState(false);

  const authHeader = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/contacts`, { headers: { Authorization: `Bearer ${token}` } });
      setContacts(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setAuthed(false);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadReviews = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/testimonials`, { headers: { Authorization: `Bearer ${token}` } });
      setReviews(data);
    } catch (e) {
      /* ignore */
    }
  }, [token]);

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setChecking(false);
        return;
      }
      try {
        await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        setAuthed(true);
        await loadContacts();
        await loadReviews();
      } catch (e) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } finally {
        setChecking(false);
      }
    };
    verify();
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, creds);
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setAuthed(true);
      const res = await axios.get(`${API}/admin/contacts`, { headers: { Authorization: `Bearer ${data.token}` } });
      setContacts(res.data);
      try {
        const rev = await axios.get(`${API}/admin/testimonials`, { headers: { Authorization: `Bearer ${data.token}` } });
        setReviews(rev.data);
      } catch (_) { /* ignore */ }
      toast.success("Willkommen zurück");
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 429 && typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error("Falsche E-Mail oder Passwort");
      }
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setContacts([]);
    setReviews([]);
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, authHeader());
      setContacts((c) => c.filter((x) => x.id !== id));
      toast.success("Nachricht gelöscht");
    } catch (e) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const moderate = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/testimonials/${id}?status=${status}`, {}, authHeader());
      setReviews((r) => r.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(status === "approved" ? "Bewertung veröffentlicht" : "Bewertung ausgeblendet");
    } catch (e) {
      toast.error("Aktion fehlgeschlagen");
    }
  };

  const removeReview = async (id) => {
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, authHeader());
      setReviews((r) => r.filter((x) => x.id !== id));
      toast.success("Bewertung gelöscht");
    } catch (e) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const refresh = () => {
    loadContacts();
    loadReviews();
  };

  // Switch tabs and auto-refresh that tab's data, so newly arrived reviews /
  // messages show without a manual refresh.
  const openTab = (next) => {
    setTab(next);
    if (next === "reviews") loadReviews();
    else loadContacts();
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <RefreshCw className="animate-spin text-bronze" size={22} />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper px-6">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-10 h-10 rounded-full border border-border flex items-center justify-center">
              <Lock size={16} className="text-bronze" />
            </span>
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-bronze">WERKVERDUNG</p>
              <h1 className="font-serif text-3xl text-ink">Nachrichten</h1>
            </div>
          </div>
          <form onSubmit={login} data-testid="admin-login-form" className="space-y-7">
            <div>
              <label className="font-mono text-[11px] uppercase tracking-widest text-bronze">E-Mail</label>
              <input
                data-testid="admin-email"
                type="email"
                required
                value={creds.email}
                onChange={(e) => setCreds({ ...creds, email: e.target.value })}
                className={field}
                placeholder="admin@…"
              />
            </div>
            <div>
              <label className="font-mono text-[11px] uppercase tracking-widest text-bronze">Passwort</label>
              <input
                data-testid="admin-password"
                type="password"
                required
                value={creds.password}
                onChange={(e) => setCreds({ ...creds, password: e.target.value })}
                className={field}
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loggingIn}
              className="w-full rounded-full bg-bronze text-paper py-4 text-sm font-semibold tracking-wide hover:bg-[#f0be6e] transition-colors disabled:opacity-60"
            >
              {loggingIn ? "…" : "Anmelden"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const pendingCount = reviews.filter((r) => r.status === "pending").length;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-paper pt-28 md:pt-32 pb-20">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between mb-8 border-b border-border pb-6">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-bronze">WERKVERDUNG · Admin</p>
            <h1 className="mt-2 font-serif text-4xl md:text-5xl text-ink">
              {tab === "messages" ? "Nachrichten" : "Bewertungen"}
            </h1>
            <p className="mt-2 text-sm text-stone">
              {tab === "messages"
                ? `${contacts.length} ${contacts.length === 1 ? "Nachricht" : "Nachrichten"}`
                : `${reviews.length} ${reviews.length === 1 ? "Bewertung" : "Bewertungen"}${pendingCount ? ` · ${pendingCount} offen` : ""}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={refresh} className="p-2.5 rounded-full border border-border text-stone hover:text-bronze hover:border-bronze transition-colors" aria-label="Refresh">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm text-ink hover:bg-bronze hover:border-bronze hover:text-paper transition-colors"
            >
              <LogOut size={15} /> Abmelden
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-10 flex gap-2">
          <button
            data-testid="admin-tab-messages"
            onClick={() => openTab("messages")}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold tracking-wide transition-colors ${
              tab === "messages" ? "bg-bronze text-paper" : "border border-border text-ink hover:border-bronze"
            }`}
          >
            <Mail size={15} /> Nachrichten
          </button>
          <button
            data-testid="admin-tab-reviews"
            onClick={() => openTab("reviews")}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold tracking-wide transition-colors ${
              tab === "reviews" ? "bg-bronze text-paper" : "border border-border text-ink hover:border-bronze"
            }`}
          >
            <MessageSquareQuote size={15} /> Bewertungen
            {pendingCount > 0 && (
              <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold text-white">
                {pendingCount}
              </span>
            )}
          </button>
        </div>

        {tab === "messages" && (
          contacts.length === 0 ? (
            <div className="py-24 text-center">
              <Mail size={28} className="mx-auto text-stone mb-4" />
              <p className="font-serif text-2xl text-ink">Noch keine Nachrichten</p>
              <p className="text-sm text-stone mt-2">Neue Anfragen über das Kontaktformular erscheinen hier.</p>
            </div>
          ) : (
            <div className="space-y-5">
              {contacts.map((c) => (
                <div key={c.id} data-testid={`admin-message-${c.id}`} className="border border-border rounded-lg p-6 hover:border-bronze/40 transition-colors">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <h3 className="font-serif text-2xl text-ink">{c.name}</h3>
                      <a href={`mailto:${c.email}`} className="text-sm text-bronze hover:underline">{c.email}</a>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-[11px] text-stone">
                        {c.created_at ? new Date(c.created_at).toLocaleString("de-DE") : ""}
                      </span>
                      <button
                        data-testid={`admin-delete-${c.id}`}
                        onClick={() => remove(c.id)}
                        className="p-2 rounded-full text-stone hover:text-red-400 transition-colors"
                        aria-label="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {c.build_type && <Tag>{c.build_type}</Tag>}
                    {c.budget && <Tag>{c.budget}</Tag>}
                    <Tag>{c.emailed ? "E-Mail gesendet" : "nur gespeichert"}</Tag>
                  </div>
                  <p className="mt-4 text-[15px] leading-relaxed text-ink/85 whitespace-pre-wrap">{c.message}</p>
                </div>
              ))}
            </div>
          )
        )}

        {tab === "reviews" && (
          reviews.length === 0 ? (
            <div className="py-24 text-center">
              <MessageSquareQuote size={28} className="mx-auto text-stone mb-4" />
              <p className="font-serif text-2xl text-ink">Noch keine Bewertungen</p>
              <p className="text-sm text-stone mt-2">Über das Bewertungsformular auf der Startseite eingereichte Stimmen erscheinen hier.</p>
            </div>
          ) : (
            <div className="space-y-5">
              {reviews.map((r) => (
                <div
                  key={r.id}
                  data-testid={`admin-review-${r.id}`}
                  className={`border rounded-lg p-6 transition-colors ${
                    r.status === "pending" ? "border-bronze/50 bg-bronze/[0.03]" : "border-border hover:border-bronze/40"
                  }`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="font-serif text-2xl text-ink">{r.name}</h3>
                        <span className="inline-flex gap-0.5" aria-label={`${r.rating} / 5`}>
                          {[1, 2, 3, 4, 5].map((n) => (
                            <Star key={n} size={14} className={n <= r.rating ? "text-bronze" : "text-border"} fill={n <= r.rating ? "currentColor" : "none"} />
                          ))}
                        </span>
                      </div>
                      <p className="mt-1 text-[13px] text-stone">{[r.company, r.service].filter(Boolean).join(" · ")}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[11px] text-stone">
                        {r.created_at ? new Date(r.created_at).toLocaleString("de-DE") : ""}
                      </span>
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider ${
                          r.status === "approved" ? "bg-green-500/15 text-green-600" : "bg-bronze/15 text-bronze"
                        }`}
                      >
                        {r.status === "approved" ? <><Check size={11} /> live</> : <><Clock size={11} /> offen</>}
                      </span>
                    </div>
                  </div>
                  <p className="mt-4 text-[15px] leading-relaxed text-ink/85 whitespace-pre-wrap">{r.quote}</p>
                  <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-border pt-4">
                    {r.status === "pending" ? (
                      <button
                        data-testid={`admin-approve-${r.id}`}
                        onClick={() => moderate(r.id, "approved")}
                        className="inline-flex items-center gap-2 rounded-full bg-bronze text-paper px-5 py-2 text-sm font-semibold hover:bg-[#f0be6e] transition-colors"
                      >
                        <Check size={15} /> Veröffentlichen
                      </button>
                    ) : (
                      <button
                        data-testid={`admin-unpublish-${r.id}`}
                        onClick={() => moderate(r.id, "pending")}
                        className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2 text-sm text-ink hover:border-bronze transition-colors"
                      >
                        <Clock size={15} /> Ausblenden
                      </button>
                    )}
                    <button
                      data-testid={`admin-review-delete-${r.id}`}
                      onClick={() => removeReview(r.id)}
                      className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2 text-sm text-stone hover:text-red-400 hover:border-red-400/40 transition-colors"
                    >
                      <Trash2 size={15} /> Löschen
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}

const Tag = ({ children }) => (
  <span className="rounded-full border border-border px-3 py-1 font-mono text-[11px] text-stone">{children}</span>
);
